import { useContext, useEffect, useMemo } from 'react'
import { type IntlShape, useIntl } from 'react-intl'
import useGlobalSearchContext from 'hooks/GlobalSearch/useGlobalSearchContext'
import type GlobalSearchState from 'contexts/GlobalSearch/GlobalSearchState'
import { GlobalSearchStateActions, GLOBAL_SEARCH_INITIAL_STATE } from 'contexts/GlobalSearch/GlobalSearchState'
import { useAppSelector } from 'hooks/reduxHooks'
import { getDefaultUserDriverCategory } from 'selectors/carHireSelectors'
import { getCarHireSearchStateFromParams } from 'components/CarHire/carHireUtils'
import GeoContext from 'contexts/geoContext'
import Places from 'constants/places'

const carHirePopularDestinationTextFormatters: Record<string, (intl: IntlShape) => string> = {
  'Sydney, NSW (SYD-Kingsford Smith Intl.)': (intl) => intl.formatMessage({
    id: 'TYUZZqq9LwFfO0nCX158',
    defaultMessage: 'Sydney, NSW (SYD-Kingsford Smith Intl.)',
    description: 'Popular car hire search suggestion label shown in the global search location input',
  }),
  'Sydney, NSW, Australia (SYD-Kingsford Smith Intl.)': (intl) => intl.formatMessage({
    id: 'E_Rv-Bvnnj_MkcOjuC1F',
    defaultMessage: 'Sydney, NSW, Australia (SYD-Kingsford Smith Intl.)',
    description: 'Popular car hire search suggestion subtitle shown in the global search location input',
  }),
  'Melbourne, VIC (MEL-Tullamarine)': (intl) => intl.formatMessage({
    id: 'QgdFm47M7oDu8z98-G6O',
    defaultMessage: 'Melbourne, VIC (MEL-Tullamarine)',
    description: 'Popular car hire search suggestion label shown in the global search location input',
  }),
  'Melbourne, VIC, Australia (MEL-Tullamarine)': (intl) => intl.formatMessage({
    id: '0RwpZ6q6i4cO7_rbTN3A',
    defaultMessage: 'Melbourne, VIC, Australia (MEL-Tullamarine)',
    description: 'Popular car hire search suggestion subtitle shown in the global search location input',
  }),
  'London (LHR-Heathrow)': (intl) => intl.formatMessage({
    id: 'BuhDQY5glWhESLIuFIKI',
    defaultMessage: 'London (LHR-Heathrow)',
    description: 'Popular car hire search suggestion label shown in the global search location input',
  }),
  'London, United Kingdom (LHR-Heathrow)': (intl) => intl.formatMessage({
    id: 'qKosK9X6EfIa-A41YSLx',
    defaultMessage: 'London, United Kingdom (LHR-Heathrow)',
    description: 'Popular car hire search suggestion subtitle shown in the global search location input',
  }),
  'Cairns': (intl) => intl.formatMessage({
    id: 'Hp1tiRhVMMdwOqW-1ZfB',
    defaultMessage: 'Cairns',
    description: 'Popular car hire search suggestion label shown in the global search location input',
  }),
  'Cairns, Queensland, Australia': (intl) => intl.formatMessage({
    id: 'EE_XJGJj3OTKMVVUBg20',
    defaultMessage: 'Cairns, Queensland, Australia',
    description: 'Popular car hire search suggestion subtitle shown in the global search location input',
  }),
  'Hobart': (intl) => intl.formatMessage({
    id: 'OqvU1P_P8H45O440Osrr',
    defaultMessage: 'Hobart',
    description: 'Popular car hire search suggestion label shown in the global search location input',
  }),
  'Tasmania, Australia': (intl) => intl.formatMessage({
    id: 'BKJj-_ZBwWh9FRYPgurF',
    defaultMessage: 'Tasmania, Australia',
    description: 'Popular car hire search suggestion subtitle shown in the global search location input',
  }),
  'Auckland': (intl) => intl.formatMessage({
    id: 'o7k8aokcR0s28vVFr_Uo',
    defaultMessage: 'Auckland',
    description: 'Popular car hire search suggestion label shown in the global search location input',
  }),
  'Auckland Region, New Zealand': (intl) => intl.formatMessage({
    id: 'wDHHFUJY7RyVaaj6g2VT',
    defaultMessage: 'Auckland Region, New Zealand',
    description: 'Popular car hire search suggestion subtitle shown in the global search location input',
  }),
  'Panama City (PTY-Tocumen Intl.)': (intl) => intl.formatMessage({
    id: 'ZSKDLAIfQhR2HBVGh7Js',
    defaultMessage: 'Panama City (PTY-Tocumen Intl.)',
    description: 'Popular car hire search suggestion label shown in the global search location input',
  }),
  'Panama City, Panama (PTY-Tocumen Intl.)': (intl) => intl.formatMessage({
    id: '7VIFjIv96WMQaCmxAUyD',
    defaultMessage: 'Panama City, Panama (PTY-Tocumen Intl.)',
    description: 'Popular car hire search suggestion subtitle shown in the global search location input',
  }),
  'Rome (FCO-Fiumicino - Leonardo da Vinci Intl.)': (intl) => intl.formatMessage({
    id: 'UIPo5u9dr4wwWr_vUgr4',
    defaultMessage: 'Rome (FCO-Fiumicino - Leonardo da Vinci Intl.)',
    description: 'Popular car hire search suggestion label shown in the global search location input',
  }),
  'Rome, Italy (FCO-Fiumicino - Leonardo da Vinci Intl.)': (intl) => intl.formatMessage({
    id: 'X2sEeD7j-0VSabcK-W65',
    defaultMessage: 'Rome, Italy (FCO-Fiumicino - Leonardo da Vinci Intl.)',
    description: 'Popular car hire search suggestion subtitle shown in the global search location input',
  }),
  'Tel Aviv': (intl) => intl.formatMessage({
    id: 'muEjZhMQQQCEJN62v5f7',
    defaultMessage: 'Tel Aviv',
    description: 'Popular car hire search suggestion label shown in the global search location input',
  }),
  'Tel Aviv District, Israel': (intl) => intl.formatMessage({
    id: 'SHrw0NpVWTnpuD-pf1ZZ',
    defaultMessage: 'Tel Aviv District, Israel',
    description: 'Popular car hire search suggestion subtitle shown in the global search location input',
  }),
  'Lisbon': (intl) => intl.formatMessage({
    id: 'z99Gwef5sLftm2k1K2rg',
    defaultMessage: 'Lisbon',
    description: 'Popular car hire search suggestion label shown in the global search location input',
  }),
  'Lisbon District, Portugal': (intl) => intl.formatMessage({
    id: 'CDiGaOmEwP2-7F9Bya3F',
    defaultMessage: 'Lisbon District, Portugal',
    description: 'Popular car hire search suggestion subtitle shown in the global search location input',
  }),
  'Munich': (intl) => intl.formatMessage({
    id: 'gA0-qlbXHakrCGNeJNyr',
    defaultMessage: 'Munich',
    description: 'Popular car hire search suggestion label shown in the global search location input',
  }),
  'Bavaria, Germany': (intl) => intl.formatMessage({
    id: 'yGtRcu0MuFqtFzK3BYZw',
    defaultMessage: 'Bavaria, Germany',
    description: 'Popular car hire search suggestion subtitle shown in the global search location input',
  }),
  'Cape Town (CPT-Cape Town Intl.)': (intl) => intl.formatMessage({
    id: 'JX7QzDjO5OM1dgdZk2W6',
    defaultMessage: 'Cape Town (CPT-Cape Town Intl.)',
    description: 'Popular car hire search suggestion label shown in the global search location input',
  }),
  'Cape Town, South Africa (CPT-Cape Town Intl.)': (intl) => intl.formatMessage({
    id: 'gpJeObhhrKYBlMQ5yWHA',
    defaultMessage: 'Cape Town, South Africa (CPT-Cape Town Intl.)',
    description: 'Popular car hire search suggestion subtitle shown in the global search location input',
  }),
  'Paphos': (intl) => intl.formatMessage({
    id: 'NlbtZhVfMK8XSNhPJx5T',
    defaultMessage: 'Paphos',
    description: 'Popular car hire search suggestion label shown in the global search location input',
  }),
  'Cyprus': (intl) => intl.formatMessage({
    id: 'TSqc-N8HtXvFt5h16mYI',
    defaultMessage: 'Cyprus',
    description: 'Popular car hire search suggestion subtitle shown in the global search location input',
  }),
  'San Francisco': (intl) => intl.formatMessage({
    id: 'gfaWJ7HJmuqZBgEYbESO',
    defaultMessage: 'San Francisco',
    description: 'Popular car hire search suggestion label shown in the global search location input',
  }),
  'California, United States of America': (intl) => intl.formatMessage({
    id: 'LvBiOSnqH6DfcBOuaBI7',
    defaultMessage: 'California, United States of America',
    description: 'Popular car hire search suggestion subtitle shown in the global search location input',
  }),
  'Los Angeles': (intl) => intl.formatMessage({
    id: 'YtUtD9n6j0I-_NMMM7Q8',
    defaultMessage: 'Los Angeles',
    description: 'Popular car hire search suggestion label shown in the global search location input',
  }),
  'Rome': (intl) => intl.formatMessage({
    id: '4rIpUzYBk4Drdo8GnWby',
    defaultMessage: 'Rome',
    description: 'Popular car hire search suggestion label shown in the global search location input',
  }),
  'Lazio, Italy': (intl) => intl.formatMessage({
    id: 'J0j7i1ljFqk4T0aqz1Og',
    defaultMessage: 'Lazio, Italy',
    description: 'Popular car hire search suggestion subtitle shown in the global search location input',
  }),
  'Athens': (intl) => intl.formatMessage({
    id: 'f46PMlr0wBSHjucbzPuB',
    defaultMessage: 'Athens',
    description: 'Popular car hire search suggestion label shown in the global search location input',
  }),
  'Attica, Greece': (intl) => intl.formatMessage({
    id: 'Tddi45y9CD4o8MxEdYZ0',
    defaultMessage: 'Attica, Greece',
    description: 'Popular car hire search suggestion subtitle shown in the global search location input',
  }),
  'Split (SPU)': (intl) => intl.formatMessage({
    id: 'd__KHya_mdBe4vh1jLTJ',
    defaultMessage: 'Split (SPU)',
    description: 'Popular car hire search suggestion label shown in the global search location input',
  }),
  'Split, Croatia (SPU)': (intl) => intl.formatMessage({
    id: 'GnU71wKo9B9Z4T5upGA2',
    defaultMessage: 'Split, Croatia (SPU)',
    description: 'Popular car hire search suggestion subtitle shown in the global search location input',
  }),
  'Marrakech (RAK-Menara)': (intl) => intl.formatMessage({
    id: '3rsqFxr8HST51NXqvVNJ',
    defaultMessage: 'Marrakech (RAK-Menara)',
    description: 'Popular car hire search suggestion label shown in the global search location input',
  }),
  'Marrakech, Morocco (RAK-Menara)': (intl) => intl.formatMessage({
    id: 'pGOYGbX6AqYqH6hKeWuO',
    defaultMessage: 'Marrakech, Morocco (RAK-Menara)',
    description: 'Popular car hire search suggestion subtitle shown in the global search location input',
  }),
  'Catania (CTA-Fontanarossa)': (intl) => intl.formatMessage({
    id: '3Db_0Q3ZezjtNE_ntHm2',
    defaultMessage: 'Catania (CTA-Fontanarossa)',
    description: 'Popular car hire search suggestion label shown in the global search location input',
  }),
  'Catania, Italy (CTA-Fontanarossa)': (intl) => intl.formatMessage({
    id: 'xr6rljG2V0Qz8rg_LLIb',
    defaultMessage: 'Catania, Italy (CTA-Fontanarossa)',
    description: 'Popular car hire search suggestion subtitle shown in the global search location input',
  }),
  'Athens (ATH-Eleftherios Venizelos)': (intl) => intl.formatMessage({
    id: 'f6Dmy2YVDfdIaiuDinsw',
    defaultMessage: 'Athens (ATH-Eleftherios Venizelos)',
    description: 'Popular car hire search suggestion label shown in the global search location input',
  }),
  'Athens, Greece (ATH-Eleftherios Venizelos)': (intl) => intl.formatMessage({
    id: 'mAYBTllDhEtGO5hnBdk7',
    defaultMessage: 'Athens, Greece (ATH-Eleftherios Venizelos)',
    description: 'Popular car hire search suggestion subtitle shown in the global search location input',
  }),
  'Valencia (VLC)': (intl) => intl.formatMessage({
    id: 'bi-dGhlxq6ahov4bttjZ',
    defaultMessage: 'Valencia (VLC)',
    description: 'Popular car hire search suggestion label shown in the global search location input',
  }),
  'Valencia, Spain (VLC)': (intl) => intl.formatMessage({
    id: '1bcbB9sGj4m9ahIgUuX5',
    defaultMessage: 'Valencia, Spain (VLC)',
    description: 'Popular car hire search suggestion subtitle shown in the global search location input',
  }),
  'Tenerife (TFS-Tenerife South)': (intl) => intl.formatMessage({
    id: 'JPgAvr18zN3Yl2JsiiAW',
    defaultMessage: 'Tenerife (TFS-Tenerife South)',
    description: 'Popular car hire search suggestion label shown in the global search location input',
  }),
  'Tenerife, Spain (TFS-Tenerife South)': (intl) => intl.formatMessage({
    id: 'gu5_S79AcEOp0L764aHr',
    defaultMessage: 'Tenerife, Spain (TFS-Tenerife South)',
    description: 'Popular car hire search suggestion subtitle shown in the global search location input',
  }),
  'Cagliari': (intl) => intl.formatMessage({
    id: 'dojNxE5i4xMDtm4loj2H',
    defaultMessage: 'Cagliari',
    description: 'Popular car hire search suggestion label shown in the global search location input',
  }),
  'Sardinia, Italy': (intl) => intl.formatMessage({
    id: 'x3zyNwRt5XW8v92Hfe9w',
    defaultMessage: 'Sardinia, Italy',
    description: 'Popular car hire search suggestion subtitle shown in the global search location input',
  }),
  'Porto': (intl) => intl.formatMessage({
    id: 'XdjiNdSxvScQdIvgj1iV',
    defaultMessage: 'Porto',
    description: 'Popular car hire search suggestion label shown in the global search location input',
  }),
  'Porto District, Portugal': (intl) => intl.formatMessage({
    id: 'Nb0uWYbIwPGlUa4WwBJR',
    defaultMessage: 'Porto District, Portugal',
    description: 'Popular car hire search suggestion subtitle shown in the global search location input',
  }),
  'Las Palmas (LPA-Gran Canaria)': (intl) => intl.formatMessage({
    id: 'DpNsXCYJ_Jz0h27dj48V',
    defaultMessage: 'Las Palmas (LPA-Gran Canaria)',
    description: 'Popular car hire search suggestion label shown in the global search location input',
  }),
  'Las Palmas, Spain (LPA-Gran Canaria)': (intl) => intl.formatMessage({
    id: 'nGtlWXDY7iDOP3fDWRTH',
    defaultMessage: 'Las Palmas, Spain (LPA-Gran Canaria)',
    description: 'Popular car hire search suggestion subtitle shown in the global search location input',
  }),
  'Madrid': (intl) => intl.formatMessage({
    id: '2tkoROMZJH6DhFSv2WA1',
    defaultMessage: 'Madrid',
    description: 'Popular car hire search suggestion label shown in the global search location input',
  }),
  'Community of Madrid, Spain': (intl) => intl.formatMessage({
    id: 'IpL2fw-PoUft89PtVvys',
    defaultMessage: 'Community of Madrid, Spain',
    description: 'Popular car hire search suggestion subtitle shown in the global search location input',
  }),
  'Málaga': (intl) => intl.formatMessage({
    id: 'UPqxjzPOVUDDiQ0NwRrh',
    defaultMessage: 'Málaga',
    description: 'Popular car hire search suggestion label shown in the global search location input',
  }),
  'Andalusia, Spain': (intl) => intl.formatMessage({
    id: 'tReGj2rDeWiagF7DTBwV',
    defaultMessage: 'Andalusia, Spain',
    description: 'Popular car hire search suggestion subtitle shown in the global search location input',
  }),
  'Kahului, HI (OGG)': (intl) => intl.formatMessage({
    id: 'RsmSYK3DiCh3ZNyB55d5',
    defaultMessage: 'Kahului, HI (OGG)',
    description: 'Popular car hire search suggestion label shown in the global search location input',
  }),
  'Kahului, HI, United States of America (OGG)': (intl) => intl.formatMessage({
    id: 'rV1xPZIJETER_AIW-GIO',
    defaultMessage: 'Kahului, HI, United States of America (OGG)',
    description: 'Popular car hire search suggestion subtitle shown in the global search location input',
  }),
  'Palm Springs, CA (PSP-Palm Springs Intl.)': (intl) => intl.formatMessage({
    id: 'pmdsALZUfvvdRCXoEyU4',
    defaultMessage: 'Palm Springs, CA (PSP-Palm Springs Intl.)',
    description: 'Popular car hire search suggestion label shown in the global search location input',
  }),
  'Palm Springs, CA, United States of America (PSP-Palm Springs Intl.)': (intl) => intl.formatMessage({
    id: 'Rga8WzzQQdLuHcxJBe2q',
    defaultMessage: 'Palm Springs, CA, United States of America (PSP-Palm Springs Intl.)',
    description: 'Popular car hire search suggestion subtitle shown in the global search location input',
  }),
  'Orlando, FL (MCO-Orlando Intl.)': (intl) => intl.formatMessage({
    id: 'hBYLXBtvid_Er8aiWchm',
    defaultMessage: 'Orlando, FL (MCO-Orlando Intl.)',
    description: 'Popular car hire search suggestion label shown in the global search location input',
  }),
  'Orlando, FL, United States of America (MCO-Orlando Intl.)': (intl) => intl.formatMessage({
    id: 'lx_kN07aGC3OaV1JSXtR',
    defaultMessage: 'Orlando, FL, United States of America (MCO-Orlando Intl.)',
    description: 'Popular car hire search suggestion subtitle shown in the global search location input',
  }),
  'Halifax': (intl) => intl.formatMessage({
    id: 'xaFlTc11grUInZweEbgG',
    defaultMessage: 'Halifax',
    description: 'Popular car hire search suggestion label shown in the global search location input',
  }),
  'Nova Scotia, Canada': (intl) => intl.formatMessage({
    id: 'FiqBuUW7ZxSUM2xQB9-E',
    defaultMessage: 'Nova Scotia, Canada',
    description: 'Popular car hire search suggestion subtitle shown in the global search location input',
  }),
  'Phoenix': (intl) => intl.formatMessage({
    id: 'ZDySDxB3p3c3-SeUimS4',
    defaultMessage: 'Phoenix',
    description: 'Popular car hire search suggestion label shown in the global search location input',
  }),
  'Arizona, United States of America': (intl) => intl.formatMessage({
    id: 'oX9JEWqRNmr7LAqFg8xt',
    defaultMessage: 'Arizona, United States of America',
    description: 'Popular car hire search suggestion subtitle shown in the global search location input',
  }),
  'Fort Lauderdale': (intl) => intl.formatMessage({
    id: 'BejZIGsHKhPDSJgZGIdl',
    defaultMessage: 'Fort Lauderdale',
    description: 'Popular car hire search suggestion label shown in the global search location input',
  }),
  'Florida, United States of America': (intl) => intl.formatMessage({
    id: 'j9xtnyFXDcwYq_y8N_Ur',
    defaultMessage: 'Florida, United States of America',
    description: 'Popular car hire search suggestion subtitle shown in the global search location input',
  }),
  'Malaga (AGP)': (intl) => intl.formatMessage({
    id: 'ro5OT61ZvuoARCFNAuxq',
    defaultMessage: 'Malaga (AGP)',
    description: 'Popular car hire search suggestion label shown in the global search location input',
  }),
  'Malaga, Spain (AGP)': (intl) => intl.formatMessage({
    id: '77AhdgWjZ468yfuHs6Vs',
    defaultMessage: 'Malaga, Spain (AGP)',
    description: 'Popular car hire search suggestion subtitle shown in the global search location input',
  }),
  'Lisbon (LIS-Humberto Delgado)': (intl) => intl.formatMessage({
    id: 'hZ6FCPUbVRDoFFTdQgrG',
    defaultMessage: 'Lisbon (LIS-Humberto Delgado)',
    description: 'Popular car hire search suggestion label shown in the global search location input',
  }),
  'Lisbon, Portugal (LIS-Humberto Delgado)': (intl) => intl.formatMessage({
    id: 'nItaL0kM6X5xFPlPKRSo',
    defaultMessage: 'Lisbon, Portugal (LIS-Humberto Delgado)',
    description: 'Popular car hire search suggestion subtitle shown in the global search location input',
  }),
  'Palermo': (intl) => intl.formatMessage({
    id: '0CA6bBN8R1IaA_xXrq7R',
    defaultMessage: 'Palermo',
    description: 'Popular car hire search suggestion label shown in the global search location input',
  }),
  'Sicily, Italy': (intl) => intl.formatMessage({
    id: 'PBbWNJMl7RoNV-fEIKEk',
    defaultMessage: 'Sicily, Italy',
    description: 'Popular car hire search suggestion subtitle shown in the global search location input',
  }),
  'Barcelona': (intl) => intl.formatMessage({
    id: 'dsCiXRpNFdyal2Hp6xof',
    defaultMessage: 'Barcelona',
    description: 'Popular car hire search suggestion label shown in the global search location input',
  }),
  'Catalonia, Spain': (intl) => intl.formatMessage({
    id: 'elmU5UPzb31OM_RdoT81',
    defaultMessage: 'Catalonia, Spain',
    description: 'Popular car hire search suggestion subtitle shown in the global search location input',
  }),
}

function formatCarHirePopularDestinationText(intl: IntlShape, text: string): string {
  return carHirePopularDestinationTextFormatters[text]?.(intl) ?? text
}

const AUSTRALIA_POPULAR_DESTINATIONS: Array<App.SearchItem> = [
  {
    searchType: 'airport',
    value: 'le_7f1eee11378becef394af4059cb8b04a',
    format: {
      mainText: 'Sydney, NSW (SYD-Kingsford Smith Intl.)',
      secondaryText: 'Sydney, NSW, Australia (SYD-Kingsford Smith Intl.)',
    },
    airportCode: 'SYD',
  },
  {
    searchType: 'airport',
    value: 'le_32b4b9f262dcc3ce114bc2e533594339',
    format: {
      mainText: 'Melbourne, VIC (MEL-Tullamarine)',
      secondaryText: 'Melbourne, VIC, Australia (MEL-Tullamarine)',
    },
    airportCode: 'MEL',
  },
  {
    searchType: 'airport',
    value: 'le_cf6d83e635ba166e4c3e55238f66d92a',
    format: {
      mainText: 'London (LHR-Heathrow)',
      secondaryText: 'London, United Kingdom (LHR-Heathrow)',
    },
    airportCode: 'LHR',
  },
  {
    searchType: 'destination',
    destinationType: 'city',
    value: Places.Cairns.id,
    format: {
      mainText: 'Cairns',
      secondaryText: 'Cairns, Queensland, Australia',
    },
  },
  {
    searchType: 'destination',
    destinationType: 'city',
    value: 'le_b573c52d599a979ba8c16e9789bb8014',
    format: {
      mainText: 'Hobart',
      secondaryText: 'Tasmania, Australia',
    },
  },
  {
    searchType: 'destination',
    destinationType: 'city',
    value: 'le_464adf7b911508701f4c4e70eab19a6e',
    format: {
      mainText: 'Auckland',
      secondaryText: 'Auckland Region, New Zealand',
    },
  },
]

const USA_POPULAR_DESTINATIONS: Array<App.SearchItem> = [
  {
    searchType: 'airport',
    value: 'le_1f504b84e3ad334dbe907d0531562ce4',
    format: {
      mainText: 'Panama City (PTY-Tocumen Intl.)',
      secondaryText: 'Panama City, Panama (PTY-Tocumen Intl.)',
    },
    airportCode: 'PTY',
  },
  {
    searchType: 'airport',
    value: 'le_4a5664bdb40e7c297f234840373a463c',
    format: {
      mainText: 'Rome (FCO-Fiumicino - Leonardo da Vinci Intl.)',
      secondaryText: 'Rome, Italy (FCO-Fiumicino - Leonardo da Vinci Intl.)',
    },
    airportCode: 'FCO',
  },
  {
    searchType: 'airport',
    value: 'le_cf6d83e635ba166e4c3e55238f66d92a',
    format: {
      mainText: 'London (LHR-Heathrow)',
      secondaryText: 'London, United Kingdom (LHR-Heathrow)',
    },
    airportCode: 'LHR',
  },
  {
    searchType: 'destination',
    destinationType: 'city',
    value: 'le_d4cd3a4f4ae9ae32b496706cb99b0a0e',
    format: {
      mainText: 'Tel Aviv',
      secondaryText: 'Tel Aviv District, Israel',
    },
  },
  {
    searchType: 'destination',
    destinationType: 'city',
    value: 'le_54ff9e9e3a2ec0300d4ce11261f5169f',
    format: {
      mainText: 'Lisbon',
      secondaryText: 'Lisbon District, Portugal',
    },
  },
  {
    searchType: 'destination',
    destinationType: 'city',
    value: 'le_3b479d2992711d569ed1edc1c9eda1ad',
    format: {
      mainText: 'Munich',
      secondaryText: 'Bavaria, Germany',
    },
  },
]

const UK_POPULAR_DESTINATIONS: Array<App.SearchItem> = [
  {
    searchType: 'airport',
    value: 'le_1b715016be3afe2d4903a8edf6a83901',
    format: {
      mainText: 'Cape Town (CPT-Cape Town Intl.)',
      secondaryText: 'Cape Town, South Africa (CPT-Cape Town Intl.)',
    },
    airportCode: 'CPT',
  },
  {
    searchType: 'destination',
    destinationType: 'city',
    value: 'le_4b92a752784b1761b28370121cfcd525',
    format: {
      mainText: 'Paphos',
      secondaryText: 'Cyprus',
    },
  },
  {
    searchType: 'destination',
    destinationType: 'city',
    value: 'le_54ff9e9e3a2ec0300d4ce11261f5169f',
    format: {
      mainText: 'Lisbon',
      secondaryText: 'Lisbon District, Portugal',
    },
  },
  {
    searchType: 'destination',
    destinationType: 'city',
    value: 'le_bd80d53a4b5d3aef9d8a1fabe2feecfc',
    format: {
      mainText: 'San Francisco',
      secondaryText: 'California, United States of America',
    },
  },
  {
    searchType: 'destination',
    destinationType: 'city',
    value: 'le_079230e29195cac1ce62e737740797a0',
    format: {
      mainText: 'Los Angeles',
      secondaryText: 'California, United States of America',
    },
  },
]

const FRANCE_POPULAR_DESTINATIONS: Array<App.SearchItem> = [
  {
    searchType: 'destination',
    destinationType: 'city',
    value: 'le_832d851fd4d8851affa0b77077f5b4d6',
    format: {
      mainText: 'Rome',
      secondaryText: 'Lazio, Italy',
    },
  },
  {
    searchType: 'destination',
    destinationType: 'city',
    value: 'le_342fdc93f26b8bb8fe3548a9188ea5d2',
    format: {
      mainText: 'Athens',
      secondaryText: 'Attica, Greece',
    },
  },
  {
    searchType: 'airport',
    value: 'le_f0c77599e690b0fc0ca20fb961ac9f7b',
    format: {
      mainText: 'Split (SPU)',
      secondaryText: 'Split, Croatia (SPU)',
    },
    airportCode: 'SPU',
  },
  {
    searchType: 'airport',
    value: 'le_8d732fc6a8c499198d0474273c3e8c46',
    format: {
      mainText: 'Marrakech (RAK-Menara)',
      secondaryText: 'Marrakech, Morocco (RAK-Menara)',
    },
    airportCode: 'RAK',
  },
  {
    searchType: 'airport',
    value: 'le_d0e7de26b52527ca29f4824eeef0e3de',
    format: {
      mainText: 'Catania (CTA-Fontanarossa)',
      secondaryText: 'Catania, Italy (CTA-Fontanarossa)',
    },
    airportCode: 'CTA',
  },
]

const GERMANY_POPULAR_DESTINATIONS: Array<App.SearchItem> = [
  {
    searchType: 'airport',
    value: 'le_040259596be66c3870f21222986d66dd',
    format: {
      mainText: 'Athens (ATH-Eleftherios Venizelos)',
      secondaryText: 'Athens, Greece (ATH-Eleftherios Venizelos)',
    },
    airportCode: 'ATH',
  },
  {
    searchType: 'airport',
    value: 'le_56cd07a539ee11783f66be26b70291ec',
    format: {
      mainText: 'Valencia (VLC)',
      secondaryText: 'Valencia, Spain (VLC)',
    },
    airportCode: 'VLC',
  },
  {
    searchType: 'airport',
    value: 'le_40381aeff0d6c4c8232541055627fa98',
    format: {
      mainText: 'Tenerife (TFS-Tenerife South)',
      secondaryText: 'Tenerife, Spain (TFS-Tenerife South)',
    },
    airportCode: 'TFS',
  },
  {
    searchType: 'destination',
    destinationType: 'city',
    value: 'le_959a557f5f6beb411fd954f3f34b21c3',
    format: {
      mainText: 'Cagliari',
      secondaryText: 'Sardinia, Italy',
    },
  },
  {
    searchType: 'destination',
    destinationType: 'city',
    value: 'le_14ecb3e647f034724f0e2d004c51d6f0',
    format: {
      mainText: 'Porto',
      secondaryText: 'Porto District, Portugal',
    },
  },
]

const SPAIN_POPULAR_DESTINATIONS: Array<App.SearchItem> = [
  {
    searchType: 'airport',
    value: 'le_4fa4ee1298896f184495015bae67404b',
    format: {
      mainText: 'Las Palmas (LPA-Gran Canaria)',
      secondaryText: 'Las Palmas, Spain (LPA-Gran Canaria)',
    },
    airportCode: 'LPA',
  },
  {
    searchType: 'airport',
    value: 'le_40381aeff0d6c4c8232541055627fa98',
    format: {
      mainText: 'Tenerife (TFS-Tenerife South)',
      secondaryText: 'Tenerife, Spain (TFS-Tenerife South)',
    },
    airportCode: 'TFS',
  },
  {
    searchType: 'destination',
    destinationType: 'city',
    value: 'le_cd18715c3998f5e305b948ffc72cf765',
    format: {
      mainText: 'Madrid',
      secondaryText: 'Community of Madrid, Spain',
    },
  },
  {
    searchType: 'destination',
    destinationType: 'city',
    value: 'le_950a4152c2b4aa3ad78bdd6b366cc179',
    format: {
      mainText: 'Málaga',
      secondaryText: 'Andalusia, Spain',
    },
  },
  {
    searchType: 'destination',
    destinationType: 'city',
    value: 'le_54ff9e9e3a2ec0300d4ce11261f5169f',
    format: {
      mainText: 'Lisbon',
      secondaryText: 'Lisbon District, Portugal',
    },
  },

]

const CANADA_POPULAR_DESTINATIONS: Array<any> = [
  {
    searchType: 'airport',
    value: 'le_d76776c4015676786316c8948f95a07b',
    format: {
      mainText: 'Kahului, HI (OGG)',
      secondaryText: 'Kahului, HI, United States of America (OGG)',
    },
    airportCode: 'OGG',
  },
  {
    searchType: 'airport',
    value: 'le_f6c0dc01b1b43ffe7d614fbb2323880c',
    format: {
      mainText: 'Palm Springs, CA (PSP-Palm Springs Intl.)',
      secondaryText: 'Palm Springs, CA, United States of America (PSP-Palm Springs Intl.)',
    },
    airportCode: 'PSP',
  },
  {
    searchType: 'airport',
    value: 'le_f6ffcdc5f396ba547fcb5843d6ba8e22',
    format: {
      mainText: 'Orlando, FL (MCO-Orlando Intl.)',
      secondaryText: 'Orlando, FL, United States of America (MCO-Orlando Intl.)',
    },
    airportCode: 'MCO',
  },
  {
    searchType: 'destination',
    destinationType: 'city',
    value: 'le_f16e5f06232a362daffcb3429e42c19d',
    format: {
      mainText: 'Halifax',
      secondaryText: 'Nova Scotia, Canada',
    },
  },
  {
    searchType: 'destination',
    destinationType: 'city',
    value: 'le_6781be9b5eb16b142d24c60a04c821e2',
    format: {
      mainText: 'Phoenix',
      secondaryText: 'Arizona, United States of America',
    },
  },
  {
    searchType: 'destination',
    destinationType: 'city',
    value: 'le_96da601313d3127d3038e0dfc095a852',
    format: {
      mainText: 'Fort Lauderdale',
      secondaryText: 'Florida, United States of America',
    },
  },
]

const ITALY_POPULAR_DESTINATIONS: Array<any> = [
  {
    searchType: 'airport',
    value: 'le_4445ee26916d5209290799c145c3d66c',
    format: {
      mainText: 'Malaga (AGP)',
      secondaryText: 'Malaga, Spain (AGP)',
    },
    airportCode: 'AGP',
  },
  {
    searchType: 'airport',
    value: 'le_a8088e2fd85e4567961e2753cf0a85d3',
    format: {
      mainText: 'Lisbon (LIS-Humberto Delgado)',
      secondaryText: 'Lisbon, Portugal (LIS-Humberto Delgado)',
    },
    airportCode: 'LIS',
  },
  {
    searchType: 'destination',
    destinationType: 'city',
    value: 'le_959a557f5f6beb411fd954f3f34b21c3',
    format: {
      mainText: 'Cagliari',
      secondaryText: 'Sardinia, Italy',
    },
  },
  {
    searchType: 'destination',
    destinationType: 'city',
    value: 'le_4ea6a546c19499318091a9df40a13181',
    format: {
      mainText: 'Palermo',
      secondaryText: 'Sicily, Italy',
    },
  },
  {
    searchType: 'destination',
    destinationType: 'city',
    value: 'le_af15ce7cbd279b1a769d85d1540a5073',
    format: {
      mainText: 'Barcelona',
      secondaryText: 'Catalonia, Spain',
    },
  },
]

const OTHER_REGIONS_POPULAR_DESTINATIONS: Array<any> = [
  {
    searchType: 'airport',
    value: 'le_7f1eee11378becef394af4059cb8b04a',
    format: {
      mainText: 'Sydney, NSW (SYD-Kingsford Smith Intl.)',
      secondaryText: 'Sydney, NSW, Australia (SYD-Kingsford Smith Intl.)',
    },
    airportCode: 'SYD',
  },
  {
    searchType: 'airport',
    value: 'le_32b4b9f262dcc3ce114bc2e533594339',
    format: {
      mainText: 'Melbourne, VIC (MEL-Tullamarine)',
      secondaryText: 'Melbourne, VIC, Australia (MEL-Tullamarine)',
    },
    airportCode: 'MEL',
  },
  {
    searchType: 'destination',
    destinationType: 'city',
    value: 'le_bd80d53a4b5d3aef9d8a1fabe2feecfc',
    format: {
      mainText: 'San Francisco',
      secondaryText: 'California, United States of America',
    },
  },
  {
    searchType: 'destination',
    destinationType: 'city',
    value: 'le_54ff9e9e3a2ec0300d4ce11261f5169f',
    format: {
      mainText: 'Lisbon',
      secondaryText: 'Lisbon District, Portugal',
    },
  },
  {
    searchType: 'destination',
    destinationType: 'city',
    value: 'le_af15ce7cbd279b1a769d85d1540a5073',
    format: {
      mainText: 'Barcelona',
      secondaryText: 'Catalonia, Spain',
    },
  },
]

const popularDestinations: Record<string, Array<App.SearchItem>> = {
  AU: AUSTRALIA_POPULAR_DESTINATIONS,
  US: USA_POPULAR_DESTINATIONS,
  GB: UK_POPULAR_DESTINATIONS,
  FR: FRANCE_POPULAR_DESTINATIONS,
  DE: GERMANY_POPULAR_DESTINATIONS,
  ES: SPAIN_POPULAR_DESTINATIONS,
  CA: CANADA_POPULAR_DESTINATIONS,
  IT: ITALY_POPULAR_DESTINATIONS,
  OTHER: OTHER_REGIONS_POPULAR_DESTINATIONS,
}

export function useGlobalCarHireSearchContext(
  initialState: GlobalSearchState = GLOBAL_SEARCH_INITIAL_STATE,
  syncWithURLSearchParams?: URLSearchParams,
) {
  const [defaultAgeCategry, defaultAge] = useAppSelector(getDefaultUserDriverCategory)
  const intl = useIntl()
  const urlState = useMemo(() => {
    if (syncWithURLSearchParams) {
      return getCarHireSearchStateFromParams(syncWithURLSearchParams)
    }
    return undefined
  }, [syncWithURLSearchParams])

  const geo = useContext(GeoContext)
  const localizedPopularDestinations = useMemo<Array<App.SearchItem>>(() => {
    const searchPopularDestinations = popularDestinations[geo.currentRegionCode] || popularDestinations.OTHER

    return searchPopularDestinations.map(destination => ({
      ...destination,
      format: {
        ...destination.format,
        mainText: formatCarHirePopularDestinationText(intl, destination.format.mainText),
        secondaryText: destination.format.secondaryText
          ? formatCarHirePopularDestinationText(intl, destination.format.secondaryText)
          : undefined,
      },
    }))
  }, [geo.currentRegionCode, intl])

  const { globalSearchState, globalSearchDispatch } = useGlobalSearchContext({
    ...initialState,
    driversAgeCategory: initialState.driversAgeCategory ?? defaultAgeCategry,
    driversAge: initialState.driversAge ?? defaultAge,
    searchVerticals: new Set(['car-hire']),
    pickUpTime: initialState.pickUpTime || '10:00',
    dropOffTime: initialState.dropOffTime || '10:00',
    popularDestinations: localizedPopularDestinations,
    isLoadingPopularDestinations: false,
  })

  useEffect(() => {
    if (urlState) {
      globalSearchDispatch({ type: GlobalSearchStateActions.SET_SEARCH_ITEM, searchItem: urlState.searchItem })
      globalSearchDispatch({ type: GlobalSearchStateActions.SET_SECONDARY_SEARCH_ITEM, searchItem: urlState.secondarySearchItem })
      globalSearchDispatch({ type: GlobalSearchStateActions.SET_CHECKIN_DATE, date: urlState.checkinDate })
      globalSearchDispatch({ type: GlobalSearchStateActions.SET_CHECKOUT_DATE, date: urlState.checkoutDate })
      globalSearchDispatch({ type: GlobalSearchStateActions.SET_DRIVERS_AGE_CATEGORY, driversAgeCategory: urlState.driversAgeCategory ?? defaultAgeCategry })
      globalSearchDispatch({ type: GlobalSearchStateActions.SET_DRIVERS_AGE, driversAge: urlState.driversAge ?? defaultAge })
      globalSearchDispatch({ type: GlobalSearchStateActions.SET_PICKUP_TIME, pickupTime: urlState.pickUpTime || '10:00' })
      globalSearchDispatch({ type: GlobalSearchStateActions.SET_RETURN_TIME, dropOffTime: urlState.dropOffTime || '10:00' })
    }
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [urlState])

  return {
    globalCarHireSearchDispatch: globalSearchDispatch,
    globalCarHireSearchState: globalSearchState,
  }
}
